import React, { useEffect, useState, useRef } from 'react';
import { featuredAPI, uploadAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import { motion } from 'framer-motion';
import { Crosshair, Video, BarChart3, Flame, Shield, Upload, CheckCircle2, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const FEATURES = [
  { slot: "personalized_drills", icon: Crosshair, title: "Personalized Drills" },
  { slot: "video_instruction", icon: Video, title: "Video Instruction" },
  { slot: "progress_tracking", icon: BarChart3, title: "Progress Tracking" },
  { slot: "coach_feedback", icon: Flame, title: "Coach Feedback" },
  { slot: "built_for_hockey", icon: Shield, title: "Built For Hockey" },
];

export default function CoachFeaturedVideos() {
  const [videos, setVideos] = useState({});
  const [uploading, setUploading] = useState(null);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const res = await featuredAPI.getAll();
      setVideos(res.data || {});
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleUploadClick = (slot) => {
    setSelectedSlot(slot);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSlot) return;

    setUploading(selectedSlot);
    try {
      const uploadRes = await uploadAPI.video(file);
      const filename = uploadRes.data.filename;

      await featuredAPI.set(selectedSlot, { video_filename: filename });
      toast.success(`Video added for ${FEATURES.find(f => f.slot === selectedSlot)?.title}`);
      await loadVideos();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Upload failed';
      toast.error(`Upload failed: ${msg}`);
    }
    setUploading(null);
    setSelectedSlot(null);
    e.target.value = '';
  };

  const handleRemove = async (slot) => {
    try {
      await featuredAPI.remove(slot);
      toast.success('Video removed');
      await loadVideos();
    } catch {
      toast.error('Failed to remove');
    }
  };

  return (
    <Layout title="FEATURED VIDEOS" showBack>
      <div className="px-4 py-6 space-y-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight" data-testid="featured-videos-title">
            LANDING PAGE VIDEOS
          </h2>
          <p className="text-xs text-[#64748B] mt-1">
            Upload preview videos for each feature on the home page. Visitors can tap to watch.
          </p>
        </motion.div>

        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={handleFileChange}
        />

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : (
          <div className="space-y-3">
            {FEATURES.map(({ slot, icon: Icon, title }, i) => {
              const hasVideo = videos[slot]?.video_filename;
              const isUploading = uploading === slot;

              return (
                <motion.div
                  key={slot}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`bg-white border rounded-xl p-4 ${
                    hasVideo ? 'border-[#10B981]/30' : 'border-[#E2E8F0]'
                  }`}
                  data-testid={`featured-slot-${slot}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      hasVideo ? 'bg-[#10B981]/15' : 'bg-[#2563EB]/15'
                    }`}>
                      {hasVideo ? (
                        <CheckCircle2 size={18} className="text-[#10B981]" />
                      ) : (
                        <Icon size={18} className="text-[#2563EB]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-['Barlow_Condensed'] text-sm font-bold">{title}</p>
                      <p className="text-[10px] text-[#64748B] truncate">
                        {hasVideo ? videos[slot].video_filename : 'No video uploaded'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {hasVideo && (
                        <button
                          onClick={() => handleRemove(slot)}
                          className="w-8 h-8 rounded-lg bg-[#FF4444]/10 flex items-center justify-center hover:bg-[#FF4444]/20 transition-colors"
                          data-testid={`remove-${slot}`}
                        >
                          <Trash2 size={14} className="text-[#FF4444]" />
                        </button>
                      )}
                      <button
                        onClick={() => handleUploadClick(slot)}
                        disabled={isUploading}
                        className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-colors ${
                          hasVideo
                            ? 'bg-[#E2E8F0] text-[#64748B] hover:bg-[#2563EB]/20 hover:text-[#2563EB]'
                            : 'bg-[#2563EB] text-white hover:bg-[#3B82F6]'
                        }`}
                        data-testid={`upload-${slot}`}
                      >
                        {isUploading ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : hasVideo ? (
                          'Replace'
                        ) : (
                          <span className="flex items-center gap-1"><Upload size={12} /> Upload</span>
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
